import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

export function TermsOfService() {
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const [returnTo, setReturnTo] = useState(null);

  useEffect(() => {
    const checkoutReturnTo = localStorage.getItem('checkoutReturnTo');
    if (checkoutReturnTo) {
      setReturnTo(checkoutReturnTo);
    }
  }, []);

  const handleBackClick = () => {
    if (returnTo) {
      // Restore checkout state and navigate back
      window.location.href = returnTo;
    } else {
      // Fallback to home
      navigate(`/${language}/`);
    }
  };

  const backText = returnTo 
    ? (t('checkout.btnBack') || 'Back') + ' to checkout'
    : t('terms.backToHome') || 'Back to home';

  return (
    <div className="min-h-screen bg-slate-950">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <button 
          onClick={handleBackClick}
          className="text-primary hover:text-purple-400 transition-colors mb-8 inline-block cursor-pointer bg-transparent border-none"
        >
          ← {backText}
        </button>
        
        <h1 className="text-4xl font-heading font-bold text-white mb-8">
          {t('terms.title')}
        </h1>

        <div className="prose prose-invert prose-slate max-w-none">
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-white mb-4">
              {t('terms.companyTitle')}
            </h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              {t('terms.companyText')}
            </p>
            <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-6 mb-4">
              <p className="text-white font-semibold mb-2">
                {t('terms.companyName')}
              </p>
              <p className="text-slate-400 mb-1">
                {t('terms.cif')}
              </p>
              <p className="text-slate-400 mb-1">
                {t('terms.address1')}
              </p>
              <p className="text-slate-400 mb-3">
                {t('terms.address2')}
              </p>
              <p className="text-slate-500 text-sm italic">
                {t('terms.companyNote')}
              </p>
            </div>
            <p className="text-slate-300 leading-relaxed">
              {t('terms.companyDesc')}
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-white mb-4">
              {t('terms.serviceModelsTitle')}
            </h2>
            <p className="text-slate-300 leading-relaxed mb-6">
              {t('terms.serviceModelsText')}
            </p>

            <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-semibold text-white mb-3">
                {t('terms.fullDevTitle')}
              </h3>
              <ul className="list-disc list-inside space-y-2 text-slate-300">
                <li>{t('terms.fullDevPrice')}</li>
                <li>{t('terms.fullDevNoSubscription')}</li>
              </ul>
              <p className="text-slate-400 mt-4">
                {t('terms.fullDevDesc')}
              </p>
            </div>

            <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-white mb-3">
                {t('terms.infraCommitTitle')}
              </h3>
              <ul className="list-disc list-inside space-y-2 text-slate-300">
                <li>{t('terms.infraSetup')}</li>
                <li>{t('terms.infraSupport')}</li>
                <li>{t('terms.infraMinimum')}</li>
              </ul>
              <p className="text-slate-400 mt-4">
                {t('terms.infraDesc')}
              </p>
              <div className="mt-4 p-4 bg-primary/10 border border-primary/20 rounded-lg">
                <p className="text-sm text-slate-300">
                  {t('terms.infraNote')}
                </p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-white mb-4">
              {t('terms.commercialStructureTitle')}
            </h2>
            <ul className="space-y-3 text-slate-300">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>{t('terms.commercialPoint1')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>{t('terms.commercialPoint2')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>{t('terms.commercialPoint3')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>{t('terms.commercialPoint4')}</span>
              </li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-white mb-4">
              {t('terms.earlyTerminationTitle')}
            </h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              {t('terms.earlyTerminationText')}
            </p>
            <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-3">
                {t('terms.calculationTitle')}
              </h3>
              <ul className="space-y-2 text-slate-300">
                <li>{t('terms.fullDevValue')}</li>
                <li>{t('terms.paidAtSetup')}</li>
                <li className="font-semibold text-white pt-2 border-t border-slate-700">
                  {t('terms.outstandingBalance')}
                </li>
              </ul>
              <p className="text-slate-400 mt-4">
                {t('terms.settlementNote')}
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-white mb-4">
              {t('terms.deliveryFrameworkTitle')}
            </h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              {t('terms.deliveryText')}
            </p>
            <ul className="space-y-2 text-slate-300">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>{t('terms.deliveryTime')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>{t('terms.acceleratedDelivery')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>{t('terms.timelinesDepend')}</span>
              </li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-white mb-4">
              {t('terms.intellectualPropertyTitle')}
            </h2>
            <p className="text-slate-300 leading-relaxed">
              {t('terms.ipText')}
            </p>
            <ul className="mt-4 space-y-2 text-slate-300">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>{t('terms.ip12Months')}</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-slate-500">{t('terms.ipOr')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>{t('terms.ipFullPayment')}</span>
              </li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-white mb-4">
              {t('terms.limitationTitle')}
            </h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              {t('terms.limitationText')}
            </p>
            <ul className="space-y-2 text-slate-300">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>{t('terms.thirdPartyInterruptions')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>{t('terms.hostingFailures')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>{t('terms.clientDelays')}</span>
              </li>
            </ul>
            <p className="text-slate-400 mt-4">
              {t('terms.limitationNote')}
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-white mb-4">
              {t('terms.governingLawTitle')}
            </h2>
            <p className="text-slate-300 leading-relaxed">
              {t('terms.governingLawText')}
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-slate-800">
          <p className="text-sm text-slate-500">
            {t('terms.lastUpdated')}
          </p>
        </div>
      </div>
    </div>
  );
}
